import type { JSX } from 'react';
import { useMemo, useState } from 'react';

import type { WithSlotProps } from '~/components/hocs';
import type { Props as RadioInputProps } from '~/components/layout/radio-input';
import type { Props as TextAreaProps } from '~/components/layout/text-area';
import { withSlot } from '~/components/hocs';
import { SlotsProvider } from '~/components/providers';
import { useTemplate } from '~/hooks';
import { cn } from '~/utils/cn';
import { renderChild } from '../../utils';

export const ID = 'ReportModal' as const;

export interface Data {
  onSubmit: (option: string, ownText?: string) => void;
}

export interface Props extends React.PropsWithChildren, WithSlotProps, Data {
  className?: string;
}

const ReportModalComponent = (props: Props) => {
  const { onSubmit } = props;
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [ownText, setOwnText] = useState('');
  const [isOwnTextTouched, setIsOwnTextTouched] = useState(false);
  const template = useTemplate('ModalReport');
  const isOwnTextInvalid = selectedOption === '4' && !ownText.trim() && isOwnTextTouched;

  const slots = useMemo(() => {
    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedOption(e.target.value);
    };
    return {
      ['option-1']: {
        value: '1',
        checked: selectedOption === '1',
        onChange,
      } satisfies Partial<RadioInputProps>,

      ['option-2']: {
        value: '2',
        checked: selectedOption === '2',
        onChange,
      } satisfies Partial<RadioInputProps>,

      ['option-3']: {
        value: '3',
        checked: selectedOption === '3',
        onChange,
      } satisfies Partial<RadioInputProps>,

      ['option-4']: {
        value: '4',
        checked: selectedOption === '4',
        onChange,
      } satisfies Partial<RadioInputProps>,

      ownText: {
        errors: isOwnTextInvalid ? ['לא יכול להיות ריק'] : [],
        onChange: e => {
          setOwnText(e.target.value);
        },
        onFocus: () => {
          setSelectedOption('4');
        },
        onBlur: () => {
          setIsOwnTextTouched(true);
        },
      } satisfies Partial<TextAreaProps>,

      submitBtn: {
        className: cn(
          (!selectedOption || (selectedOption === '4' && (!ownText.trim() || isOwnTextInvalid))) && 'disabled'
        ),
        onClick: () => {
          onSubmit(selectedOption, selectedOption === '4' ? ownText.trim() : undefined);
        },
      } satisfies JSX.IntrinsicElements['button'],
    };
  }, [selectedOption, ownText, isOwnTextInvalid, onSubmit]);

  return <SlotsProvider slots={slots}>{renderChild(template)}</SlotsProvider>;
};

export const ReportModal = withSlot(ReportModalComponent);
